import { useEffect, useRef } from "react";
import ahoy from "@lib/ahoy";
import { analytics } from "@lib/trackers/analytics";
import { useLocale } from "@lib/i18n/navigation-pages-router";
import { useRouter } from "next/router";
import { defaultLocale } from "@lib/i18n/config";
import Slider from "@components/core/Slider";
import ProductImageLink from "@components/views/Product/Shared/ProductImageLink";
import ProductLike from "@components/views/Product/Shared/ProductLike";
import {
  useCatalogProductSliderLastScrollX,
  useSetCatalogLastScrollY,
  useSetCatalogProductSliderLastScrollX,
} from "@hooks/use-catalog-navigation-hooks";
import { clearCatalogProductSliderLastScrollX } from "@actions/navigation-actions";

function LowResultsProductSliderGallery({
  context = "",
  products = [],
  uiState,
  hub,
  listingPage,
}) {
  const router = useRouter();
  const locale = useLocale();
  const productSliderRef = useRef();
  const setCatalogLastScrollY = useSetCatalogLastScrollY();
  const setCatalogProductSliderLastScrollX = useSetCatalogProductSliderLastScrollX();
  const productSliderLastScrollX = useCatalogProductSliderLastScrollX(context);

  function sendTrackerData(product, index) {
    const defaultEventData = {
      tracking_name: "lowResultsProductSlider",
      page: router.asPath,
      hub_slug: hub?.slug || listingPage?.hub?.slug,
      listing_slug: listingPage?.slug,
      search_with_low_results: uiState,
      position: index + 1,
      product_slug: product.slug,
    };

    ahoy("$click", defaultEventData);
    analytics.track("$click", defaultEventData);
  }

  function setSliderRef(ref) {
    productSliderRef.current = ref;
  }

  function handleClick(product, index) {
    const { scrollY } = window;
    setCatalogLastScrollY(scrollY);
    setCatalogProductSliderLastScrollX(productSliderRef?.current?.scrollLeft, context);

    sendTrackerData(product, index);
  }

  function productsList() {
    return products.map((product, index) => (
      <section
        className="low-results-product-slider-section-card"
        key={product.id}
      >
        <div
          key={product.id}
          className="low-results-product-slider-section-card-picture"
        >
          <ProductImageLink
            algolia={products.meta?.algolia}
            product={product}
            onClick={() => handleClick(product, index)}
          />
          <ProductLike
            product={product}
            className="catalog-product-like"
          />
        </div>
        <div className="low-results-product-slider-section-card-text-container">
          <h6 className="mb-0 low-results-product-slider-section-card-text">
            {product[locale === defaultLocale ? "name" : `name_${locale.toLowerCase()}`]}
          </h6>
          <p className="mb-0 text-gray low-results-product-slider-section-card-text">
            {product.brand.name}
          </p>
        </div>
      </section>
    ));
  }

  function scrollToLastPosition() {
    const left = productSliderLastScrollX;
    productSliderRef?.current?.scrollTo({ left, behavior: "smooth" });

    if (left > 0) clearCatalogProductSliderLastScrollX();
  }

  useEffect(() => {
    scrollToLastPosition();
  }, []);

  return (
    <Slider
      name="low-results-product-slider"
      setSliderRef={setSliderRef}
      cards={productsList()}
      productSliderRef={productSliderRef}
    />
  );
}

export default LowResultsProductSliderGallery;
